// Phase 3 — useCopilotSession (Task 3.3).
//
// The UNIFIED consumer for a boat's Copilot Session. It owns ONE subscription to
// the multiplexed per-boat channel `tenant.{tenantId}.copilot.{boatId}` and
// turns the two coexisting envelope shapes into a chat-style feed + the proven
// per-file rows + a run-level terminal:
//
//   1. LIVE (ephemeral, NO `seq`) — published directly by ai-engine/Laravel:
//      - `field` (a document read, never a photo one) → a progress LINE
//        ("Reading the invoice… page 1… found HIN …", #1178); `segments` → a
//        "Reading the invoice…" line; `insight` → a live insight;
//        `phase_done` → the finished phase; `entity` → ignored.
//        Form values never come from here: useCanvasHydration re-reads the boat.
//      - `status` → upsert a per-file in-progress ROW (the spinner).
//      - `done`/`done_with_errors`/`failed` → the live TERMINAL.
//   2. LOGGED (persisted, HAS a numeric `seq`) — the CopilotEvents envelope, with
//      type-specific data under `payload`:
//      - `run_terminal` → terminal (REPLAYABLE); status/failed_count from payload.
//      - `finding`/`step`/`insight`/`job` → an assistant FEED item.
//
// DISCRIMINATOR: a numeric `seq` AND a `payload` object ⇒ logged (read
// `payload`, track `lastSeq`); otherwise ⇒ live (read top level). Live
// micro-fills also carry a (per-job) `seq` but no `payload`, so seq alone would
// misclassify them and corrupt the per-boat cursor + dedupe set.
//
// This hook LIFTS useExtractionStatus's proven machinery verbatim — the per-file
// row map, the stale-epoch guard (drop lower epoch, reset on higher epoch), the
// monotonic terminal flags, and the hasSubscribedRef reconnect pattern — and
// swaps the D2 reconcile for a replay-after-seq via fetchCopilotEvents. On
// (re)connect it replays the gap (after=lastSeq); applied events are deduped by
// `seq` so a replay never double-applies a terminal or duplicates a feed item.

import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useChannel } from '@/lib/realtime/useChannel';
import {
  fetchCopilotEvents,
  type CopilotEventEnvelope,
} from '@/modules/inventory/api/copilotEventsApi';

// ---------------------------------------------------------------------------
// Per-file row state — lifted verbatim from useExtractionStatus.
// ---------------------------------------------------------------------------
export type ExtractionRowState =
  | 'queued'
  | 'classifying'
  | 'reading'
  | 'done'
  | 'failed';

export interface StatusRow {
  mediaId: number | string;
  name?: string;
  kind?: 'image' | 'document';
  state: ExtractionRowState;
  label?: string;
  userMessage?: string;
}

/** An assistant bubble in the chat thread (logged narrative event). */
export interface FeedItem {
  id: string;
  role: 'assistant';
  content: string;
  timestamp: string | null;
  type: 'finding' | 'step' | 'insight' | 'job';
  icon?: string;
  /** The logged seq this item was derived from (ordering + dedupe key). */
  seq: number;
  /**
   * The raw logged event `payload` (§3B addition). The feed UI needs this to
   * derive the right per-bubble affordance — both per-file read findings AND
   * conflict findings arrive as `type:'finding'`, so the renderer keys off
   * `payload.detail?.reason === 'conflict'` to show the ⚠ (conflict) vs ✓ (read)
   * icon. Carried verbatim so the rendering logic lives in the feed component.
   */
  payload: Record<string, unknown>;
}

export interface CopilotTerminal {
  isComplete: boolean;
  isFailed: boolean;
  isDoneWithErrors: boolean;
  headline: string | null;
}

/** #1178 — one line of live text (keyed for dedupe). */
export interface ProgressLine {
  key: string;
  text: string;
}

export interface CopilotSession {
  feed: FeedItem[];
  rows: StatusRow[];
  terminal: CopilotTerminal;
  failedCount: number;
  lastSeq: number;
  /** #1178 (spec §2) — the latest document value reads, newest last (at most 5). */
  progress: ProgressLine[];
  /** Live insights (complete ones, #1243), newest last (at most 5). */
  insights: ProgressLine[];
  /** The phase this run last finished (`phase_done`), or null. */
  phaseDone: string | null;
}

const HEADLINE_PROCESSING = 'Reading your documents…';
const HEADLINE_DONE = 'All set – review your listing';
const HEADLINE_DONE_WITH_ERRORS =
  'Some files couldn’t be read – review your listing';
const HEADLINE_FAILED = 'We couldn’t finish reading your files';

const LIVE_LINES_MAX = 5;

/** #1178 — document types as the progress text names them. */
const PROGRESS_DOC_LABELS: Record<string, string> = {
  invoice: 'invoice',
  coo_boat: 'boat certificate of origin',
  coo_trailer: 'trailer certificate of origin',
  bill_of_lading: 'bill of lading',
  transfer: 'title transfer',
};

/** The boat values worth a progress line, as the dealer reads them (decision D41). */
const PROGRESS_FIELD_LABELS: Record<string, string> = {
  hin: 'HIN',
  manufacturer: 'make',
  model: 'model',
  model_year: 'year',
  length_ft: 'length',
  beam_in: 'beam',
  exterior_color: 'exterior color',
};

/**
 * A live `field` event as a progress line, with the page the value came from
 * (`source.page`, decision D51). A photo read is a suggestion (#1179): never "found".
 */
function progressLine(event: Record<string, unknown>): ProgressLine | null {
  if (event.source_kind === 'photo') return null;
  if (event.entity_ref !== 'boat#0' || typeof event.path !== 'string') return null;
  const label = PROGRESS_FIELD_LABELS[event.path];
  const value = event.value;
  if (!label || value === null || value === undefined || value === '') return null;
  const source = event.source as { doc_type?: unknown; page?: unknown } | null | undefined;
  const docType = typeof source?.doc_type === 'string' ? source.doc_type : '';
  const document = PROGRESS_DOC_LABELS[docType] ?? 'document';
  const page = typeof source?.page === 'number' ? ` page ${source.page}…` : '';
  return { key: `boat#0.${event.path}`, text: `Reading the ${document}…${page} found ${label} ${String(value)}` };
}

/** The streamed `segments` event: one "Reading the invoice…" line per document the read identified. */
function documentLines(event: Record<string, unknown>): ProgressLine[] {
  const documents = Array.isArray(event.documents) ? event.documents : [];
  return documents.flatMap((doc) => {
    const docType = doc && typeof doc === 'object' && typeof (doc as { doc_type?: unknown }).doc_type === 'string'
      ? (doc as { doc_type: string }).doc_type
      : null;
    return docType ? [{ key: `doc:${docType}`, text: `Reading the ${PROGRESS_DOC_LABELS[docType] ?? 'document'}…` }] : [];
  });
}

function insightLine(event: Record<string, unknown>): ProgressLine | null {
  if (typeof event.text !== 'string' || event.text.trim() === '') return null;
  const kind = typeof event.kind === 'string' ? event.kind : '';
  return { key: `${kind}:${event.text}`, text: event.text };
}

/** Replace a line with the same key (it moves to the end) and keep the newest few. */
function pushLine(lines: ProgressLine[], line: ProgressLine): ProgressLine[] {
  return [...lines.filter((existing) => existing.key !== line.key), line].slice(-LIVE_LINES_MAX);
}

/** A processing (non-terminal-per-item) row keeps the headline in the reading state. */
function isProcessingState(s: ExtractionRowState): boolean {
  return s === 'queued' || s === 'classifying' || s === 'reading';
}

/**
 * Carry-forward (d) from useExtractionStatus: the backend's `status` event no
 * longer sends a human `label`. Derive a brief, user-safe fallback for the
 * in-progress states.
 */
function deriveLabel(
  state: ExtractionRowState | undefined,
  kind: 'image' | 'document' | undefined,
): string | undefined {
  if (state === 'classifying') return 'Analyzing photos…';
  if (state === 'reading') {
    return kind === 'image' ? 'Analyzing photo…' : 'Reading document…';
  }
  return undefined;
}

/**
 * Human content for a logged narrative event from its payload. `finding`/`job`
 * carry a `title`; routine `step`s carry a `step` label; `insight`s carry
 * `text`/`summary`. Falls back to the type so a bubble is never empty.
 */
function feedContent(
  type: FeedItem['type'],
  payload: Record<string, unknown>,
): string {
  const pick = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = payload[k];
      if (typeof v === 'string' && v.length > 0) return v;
    }
    return undefined;
  };
  return (
    pick('title', 'step', 'text', 'summary', 'detail', 'label', 'message') ??
    type
  );
}

// ---------------------------------------------------------------------------
// Reducer — the row map (keyed by server media_id) + the feed (keyed/ordered by
// seq) + monotonic terminal flags + failedCount. A single reducer keeps the
// whole session atomic and reset-on-channel-change in one place.
// ---------------------------------------------------------------------------
interface ReducerState {
  /** media_id -> row. Keyed SOLELY by the server media_id (WARNING-5). */
  byMediaId: Map<number | string, StatusRow>;
  /** Insertion order of media_id keys for stable row ordering. */
  order: (number | string)[];
  /** seq -> feed item (the seq is the dedupe + order key). */
  feedBySeq: Map<number, FeedItem>;
  isComplete: boolean;
  isDoneWithErrors: boolean;
  isFailed: boolean;
  failedCount: number;
  progress: ProgressLine[];
  insights: ProgressLine[];
  phaseDone: string | null;
}

type Action =
  | { kind: 'upsert'; row: StatusRow }
  | { kind: 'feed'; item: FeedItem }
  | { kind: 'complete' }
  | { kind: 'complete_with_errors'; failedCount?: number }
  | { kind: 'fail'; failedCount?: number }
  | { kind: 'reset-epoch' }
  | { kind: 'reset' }
  | { kind: 'progress'; line: ProgressLine }
  | { kind: 'insight'; line: ProgressLine }
  | { kind: 'phase'; phase: string };

function emptyReducerState(): ReducerState {
  return {
    byMediaId: new Map(),
    order: [],
    feedBySeq: new Map(),
    isComplete: false,
    isDoneWithErrors: false,
    isFailed: false,
    failedCount: 0,
    progress: [],
    insights: [],
    phaseDone: null,
  };
}

function reducer(state: ReducerState, action: Action): ReducerState {
  switch (action.kind) {
    case 'upsert': {
      // UPSERT a row KEYED BY media_id (lifted from useExtractionStatus). A
      // status for a not-yet-seeded media_id CREATES the row. Seeded name/kind
      // survive a status that omits them.
      const byMediaId = new Map(state.byMediaId);
      const order = [...state.order];
      const key = action.row.mediaId;
      const prev = byMediaId.get(key);
      if (!prev) order.push(key);
      byMediaId.set(key, {
        mediaId: key,
        name: action.row.name ?? prev?.name,
        kind: action.row.kind ?? prev?.kind,
        state: action.row.state,
        label: action.row.label ?? prev?.label,
        userMessage: action.row.userMessage ?? prev?.userMessage,
      });
      return { ...state, byMediaId, order };
    }
    case 'feed': {
      // Append an assistant bubble, deduped by seq. A repeat seq (replay) is a
      // no-op (we keep the first-applied item).
      if (state.feedBySeq.has(action.item.seq)) return state;
      const feedBySeq = new Map(state.feedBySeq);
      feedBySeq.set(action.item.seq, action.item);
      return { ...state, feedBySeq };
    }
    case 'complete':
      // MONOTONIC terminal — only turns flags ON, never OFF.
      return { ...state, isComplete: true };
    case 'complete_with_errors':
      return {
        ...state,
        isComplete: true,
        isDoneWithErrors: true,
        failedCount:
          action.failedCount != null ? action.failedCount : state.failedCount,
      };
    case 'fail':
      return {
        ...state,
        isFailed: true,
        failedCount:
          action.failedCount != null ? action.failedCount : state.failedCount,
      };
    case 'reset-epoch':
      // A new run (higher epoch) — clear the prior run's rows + terminal flags
      // but keep the feed history (the chat thread is a persistent timeline).
      return {
        ...emptyReducerState(),
        feedBySeq: state.feedBySeq,
      };
    case 'progress':
      return { ...state, progress: pushLine(state.progress, action.line) };
    case 'insight':
      return { ...state, insights: pushLine(state.insights, action.line) };
    case 'phase':
      return { ...state, phaseDone: action.phase };
    case 'reset':
      return emptyReducerState();
    default:
      return state;
  }
}

/** A LIVE event payload (status / terminal). Logged events are read separately. */
interface LiveEvent {
  type?: string;
  epoch?: number;
  media_id?: number | string;
  state?: ExtractionRowState;
  label?: string;
  user_message?: string;
}

/**
 * Consume the unified Copilot channel into a chat feed + per-file rows + a
 * run-level terminal, replaying logged events on (re)connect.
 *
 * @param tenantId  tenant id (null while unknown → no subscription)
 * @param boatId    inventory_item_id (the Copilot session anchor)
 */
export function useCopilotSession(
  tenantId: string | null | undefined,
  boatId: string | null | undefined,
): CopilotSession {
  const [state, dispatch] = useReducer(reducer, undefined, emptyReducerState);

  // Current epoch — events from a lower epoch are stale and ignored (Pitfall 5).
  const currentEpochRef = useRef<number | null>(null);

  // Reconnect guard (lifted): the FIRST onSubscribed only ARMS this ref (the
  // mount effect owns the initial replay); every SUBSEQUENT onSubscribed is a
  // genuine reconnect and replays the gap.
  const hasSubscribedRef = useRef(false);

  // The set of LOGGED seqs already applied (dedupe across live + replay) and the
  // replay cursor (max applied seq). Refs so the stable channel handler reads
  // live values without re-binding, and so an async replay sees fresh values.
  const seenSeqsRef = useRef<Set<number>>(new Set());
  const lastSeqRef = useRef(0);

  // Latest reducer state mirrored into a ref so the channel handler can read a
  // seeded row's `kind` for the label fallback without re-binding.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Reset on channel identity change (switching boats clears everything).
  useEffect(() => {
    currentEpochRef.current = null;
    hasSubscribedRef.current = false;
    seenSeqsRef.current = new Set();
    lastSeqRef.current = 0;
    dispatch({ kind: 'reset' });
     
  }, [tenantId, boatId]);

  const channel: string | null =
    tenantId && boatId ? `tenant.${tenantId}.copilot.${boatId}` : null;

  // Active channel mirrored into a ref so an in-flight replay can detect a boat
  // switch (the race guard) when it resolves.
  const activeChannelRef = useRef(channel);
  activeChannelRef.current = channel;

  // Apply ONE epoch-bearing terminal (live `done`/`done_with_errors`/`failed`
  // OR logged `run_terminal`), epoch-guarded + monotonic. Returns false if the
  // event was stale (caller may still account for a lower-priority effect).
  const applyEpochGuard = useCallback((epoch: number | undefined): boolean => {
    const eventEpoch =
      typeof epoch === 'number' ? epoch : currentEpochRef.current;
    const current = currentEpochRef.current;
    if (
      typeof eventEpoch === 'number' &&
      typeof current === 'number' &&
      eventEpoch < current
    ) {
      return false; // stale — drop.
    }
    if (
      typeof eventEpoch === 'number' &&
      (current === null || eventEpoch > current)
    ) {
      if (current !== null && eventEpoch > current) {
        dispatch({ kind: 'reset-epoch' });
      }
      currentEpochRef.current = eventEpoch;
    }
    return true;
  }, []);

  // Apply a terminal status (shared by the live and logged paths). Monotonic
  // flags make a repeat application idempotent.
  const applyTerminalStatus = useCallback(
    (status: string | undefined, failedCount: number | undefined) => {
      if (status === 'done') {
        dispatch({ kind: 'complete' });
      } else if (status === 'done_with_errors') {
        dispatch({ kind: 'complete_with_errors', failedCount });
      } else if (status === 'failed' || status === 'fail') {
        dispatch({ kind: 'fail', failedCount });
      }
    },
    [],
  );

  // Apply a LOGGED envelope (replay or live). Deduped by seq; advances the
  // replay cursor. run_terminal → terminal; finding/step/insight/job → feed.
  const applyLogged = useCallback(
    (env: CopilotEventEnvelope) => {
      const seq = env.seq;
      if (typeof seq !== 'number') return; // not actually logged.

      const payload = (env.payload ?? {}) as Record<string, unknown>;

      // Recognized logged types ONLY. Mutating the cursor/dedupe set is deferred
      // until the type is matched so an unrecognized shape that slips past the
      // payload-presence discriminator can never poison the per-boat replay
      // cursor or the dedupe set (belt-and-suspenders behind that fix).
      if (
        env.type !== 'run_terminal' &&
        env.type !== 'finding' &&
        env.type !== 'step' &&
        env.type !== 'insight' &&
        env.type !== 'job' &&
        env.type !== 'chat'
      ) {
        return;
      }

      if (seenSeqsRef.current.has(seq)) return; // dedupe.
      seenSeqsRef.current.add(seq);
      if (seq > lastSeqRef.current) lastSeqRef.current = seq;

      if (env.type === 'run_terminal') {
        if (!applyEpochGuard(env.epoch)) return;
        const status =
          typeof payload.status === 'string' ? payload.status : undefined;
        const failedCount =
          typeof payload.failed_count === 'number'
            ? payload.failed_count
            : undefined;
        applyTerminalStatus(status, failedCount);
        return;
      }

      if (
        env.type === 'finding' ||
        env.type === 'step' ||
        env.type === 'insight' ||
        env.type === 'job'
      ) {
        const icon = typeof payload.icon === 'string' ? payload.icon : undefined;
        dispatch({
          kind: 'feed',
          item: {
            id: env.id || `${env.type}:${seq}`,
            role: 'assistant',
            content: feedContent(env.type, payload),
            timestamp: env.ts ?? null,
            type: env.type,
            icon,
            seq,
            payload,
          },
        });
      }
      // chat is a recognized logged type (advances the cursor) but is not
      // surfaced in this feed.
    },
    [applyEpochGuard, applyTerminalStatus],
  );

  // Replay the gap after the cursor. The active channel is captured at call
  // time; a boat switch before the fetch resolves DROPS the response. A failed
  // replay never throws.
  const replay = useCallback(
    (boatIdArg: string, channelAtCall: string | null) => {
      void fetchCopilotEvents(boatIdArg, lastSeqRef.current)
        .then((events) => {
          if (activeChannelRef.current !== channelAtCall) return; // boat switched — drop.
          for (const env of events) applyLogged(env);
        })
        .catch((err) => {
          console.warn('[useCopilotSession] replay failed', err);
        });
    },
    [applyLogged],
  );

  // Replay once on mount (and on channel change), so a session opened AFTER the
  // run finished still shows the persisted feed + terminal.
  useEffect(() => {
    if (!boatId || !channel) return;
    replay(boatId, channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, boatId]);

  useChannel<Record<string, unknown>>(channel, {
    // Reconnect-only (mirrors useExtractionStatus): the FIRST onSubscribed only
    // arms the guard (the mount effect owns the initial replay); subsequent ones
    // are genuine reconnects and replay the gap a dropped connection missed.
    onSubscribed: () => {
      if (!hasSubscribedRef.current) {
        hasSubscribedRef.current = true;
        return;
      }
      if (boatId && channel) replay(boatId, channel);
    },
    onPublication: (env) => {
      // ai-engine publishes raw at ctx.data; Laravel broadcasts wrap in
      // {event, payload}. Unwrap defensively (mirror useFactoryPacketFill).
      const rawOuter = env as unknown as Record<string, unknown>;
      const raw: Record<string, unknown> =
        rawOuter &&
        typeof rawOuter === 'object' &&
        ('type' in rawOuter || 'seq' in rawOuter)
          ? rawOuter
          : ((rawOuter as { payload?: Record<string, unknown> })?.payload ?? {});

      // DISCRIMINATOR: a LOGGED CopilotEvents envelope has BOTH a numeric `seq`
      // (the per-boat replay cursor) AND a `payload` object (its type-specific
      // data). Live ai-engine micro-fills (field/entity/segments/insight) also
      // carry a numeric `seq` — but a per-JOB one, with NO `payload` — so seq
      // alone misclassifies them as logged and corrupts the per-boat cursor +
      // dedupe set. Require the payload object; live events fall through below.
      if (
        typeof raw.seq === 'number' &&
        raw.payload !== null &&
        typeof raw.payload === 'object'
      ) {
        applyLogged(raw as unknown as CopilotEventEnvelope);
        return;
      }

      const event = raw as unknown as LiveEvent;

      // #1178 (spec §2) — document reads become progress text; they never write
      // the form (useCanvasHydration re-reads the merged boat).
      if (event.type === 'field') {
        const line = progressLine(raw);
        if (line) dispatch({ kind: 'progress', line });
        return;
      }
      if (event.type === 'segments') {
        for (const line of documentLines(raw)) dispatch({ kind: 'progress', line });
        return;
      }
      if (event.type === 'insight') {
        const line = insightLine(raw);
        if (line) dispatch({ kind: 'insight', line });
        return;
      }
      if (event.type === 'phase_done') {
        if (!applyEpochGuard(event.epoch)) return;
        dispatch({ kind: 'phase', phase: typeof raw.phase === 'string' ? raw.phase : '' });
        return;
      }

      // entity / partial are not shown.
      if (
        event.type !== 'status' &&
        event.type !== 'done' &&
        event.type !== 'done_with_errors' &&
        event.type !== 'failed'
      ) {
        return;
      }

      if (event.type === 'status') {
        if (event.media_id == null) return;
        if (!applyEpochGuard(event.epoch)) return;
        const rowState = event.state ?? 'reading';
        const kind = stateRef.current.byMediaId.get(event.media_id)?.kind;
        dispatch({
          kind: 'upsert',
          row: {
            mediaId: event.media_id,
            state: rowState,
            label: event.label ?? deriveLabel(rowState, kind),
            userMessage: event.user_message,
          },
        });
        return;
      }

      // Live terminal — epoch-guarded the same way as the logged run_terminal.
      if (!applyEpochGuard(event.epoch)) return;
      applyTerminalStatus(event.type, undefined);
    },
  });

  // When the session is terminal, no file is still processing — so project any
  // row still in an in-progress state (queued/classifying/reading) to `done`.
  // This restores the old extractionComplete net (Phase 3B dropped it): a
  // `reading` row that never got a per-file `done` (e.g. a dropped PDF) must not
  // keep spinning after the run terminal. Pure derivation — the reducer state is
  // untouched; explicit `done`/`failed` rows are left exactly as reported.
  const sessionTerminal =
    state.isComplete || state.isDoneWithErrors || state.isFailed;
  const rows = state.order
    .map((key) => state.byMediaId.get(key))
    .filter((r): r is StatusRow => r != null)
    .map((r) =>
      sessionTerminal && isProcessingState(r.state)
        ? { ...r, state: 'done' as ExtractionRowState }
        : r,
    );

  const feed = Array.from(state.feedBySeq.values()).sort(
    (a, b) => a.seq - b.seq,
  );

  const anyProcessing = rows.some((r) => isProcessingState(r.state));
  // failedCount: the explicit run_terminal failed_count when present, else the
  // count of per-file failed rows (the live-only fallback).
  const rowFailedCount = rows.filter((r) => r.state === 'failed').length;
  const failedCount =
    state.failedCount > 0 ? state.failedCount : rowFailedCount;

  // Headline: failed wins, then partial, then done, then processing. Null while
  // nothing has happened yet (no rows, no terminal) so the UI can stay quiet.
  let headline: string | null;
  if (state.isFailed) {
    headline = HEADLINE_FAILED;
  } else if (state.isDoneWithErrors && !anyProcessing) {
    headline = HEADLINE_DONE_WITH_ERRORS;
  } else if (state.isComplete && !anyProcessing) {
    headline = HEADLINE_DONE;
  } else if (rows.length > 0) {
    headline = HEADLINE_PROCESSING;
  } else {
    headline = null;
  }

  return {
    feed,
    rows,
    terminal: {
      isComplete: state.isComplete,
      isFailed: state.isFailed,
      isDoneWithErrors: state.isDoneWithErrors,
      headline,
    },
    failedCount,
    lastSeq: lastSeqRef.current,
    progress: state.progress,
    insights: state.insights,
    phaseDone: state.phaseDone,
  };
}
